import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, Autocomplete, Grid, Typography, Button, DialogActions, Checkbox, Tooltip } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import SimpleAutocompleteProps from '../../../../reusable-components/autocomplete/SimpleAutocompleteProps';
import axiosInstance from '../../../../utils/axios';
import { objectToFormData } from '../../../../api/utils';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { COPYUSERS_COLUMNS } from '../../../../reusable-components/datagrid/permissionColumns';

CopyUserModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  users: PropTypes.array
}

export default function CopyUserModal({ open, setOpen, users}) {
  const { enqueueSnackbar } = useSnackbar();

  const [userList, setUserList] = useState([]);
  const [copyUser, setCopyUser] = useState(null);
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [copyFacilityManager, setCopyFacilityManager] = useState(false);

  useEffect(() => { setCopyUser(null); setSelected([]); setCopyFacilityManager(false) }, [open]);
  useEffect(() => { setUserList(users.map(user => ({ value: user.id, label: `${user.firstName} ${user.lastName} (${user.email})` }))); }, [users]);

  const submit = () => {
    if (submitting) return;
    setSubmitting(true);
    axiosInstance.post("Admin/User/CopyUsersRoles", objectToFormData({ copyUserId: copyUser.value, updateUsers: selected, copyFacilityManager }))
      .then(() => {
        enqueueSnackbar("User roles updated.", { variant: "success" });
        setOpen(false);
      })
      .catch(() => { enqueueSnackbar("Users roles update failed", { variant: "error" }); })
      .finally(() => setSubmitting(false));
  }

  return <Dialog
    open={open}
    onClose={(event, reason) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      setOpen(false);
    }}
    maxWidth="xl"
    fullWidth
  >
    <DialogTitle sx={{ textAlign: 'center' }}>
      Copy User Permissions/Roles
      <CloseIconButton onClick={() => setOpen(false)} />
    </DialogTitle>
    <DialogContent>
      <Grid sx={{ mt: 0 }} container spacing={2}>       
        <Grid item xs={12}>
          <Typography>Copy From User</Typography>
          <Autocomplete
            fullWidth
            value={copyUser}
            onChange={(event, newValue) => setCopyUser(newValue)}
            options={userList}
            {...SimpleAutocompleteProps}
          />
        </Grid>
        <Grid item xs={12}>
          <Tooltip arrow="top" title="Select this if the users should manage the same facilities as the user you’re copying from. Leave it unchecked to copy permissions only.">
            <span>
              <Checkbox disableRipple sx={{ pl: 0 }} checked={copyFacilityManager} onChange={(e) => setCopyFacilityManager(e.target.checked)} />
              Copy facility manager role & assignments
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Typography>Users To Update</Typography>        
          <CustomDataGrid
            gridId="admin-copy-users-view"
            scrollbarHeight={null}
            tableBoxSX={{ height: '375px' }}
            boxSX={{ height: '425px' }}
            data={users}
            gridColumns={COPYUSERS_COLUMNS}
            checkboxSelection
            onRowSelectionModelChange={(newSelection) => setSelected(newSelection?.length < 11 ? newSelection : newSelection.splice(0, 10))}
            rowSelectionModel={selected}  
          />
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={() => setOpen(false)}>
        Cancel
      </Button>
      <LoadingButton
        variant="contained"
        color="primary"
        size="medium"
        disabled={!copyUser || !selected?.length}
        sx={{ width: 150 }}
        onClick={submit}
        loading={submitting}
      >
        Save
      </LoadingButton>
    </DialogActions>
  </Dialog>;
}
